/**
 * Microphone samples to a WAV file the transcriber accepts: 16 kHz, mono,
 * 16-bit PCM. WAV rather than the browser's own webm because docs/openrouter.md
 * lists WAV and does not list webm. Pure, so it is tested without a microphone.
 */
export const WAV_RATE = 16_000;
const HEADER_BYTES = 44;

/**
 * From the device's rate (usually 44.1 or 48 kHz) down to `to`, averaging the
 * samples each output sample stands for — a cheap low-pass that keeps speech
 * intelligible without aliasing hiss.
 */
export function downsample(input: Float32Array, from: number, to: number = WAV_RATE): Float32Array {
  if (from === to) return input.slice();
  if (from < to) throw new Error(`cannot upsample from ${from} Hz to ${to} Hz`);
  const ratio = from / to;
  const length = Math.floor(input.length / ratio);
  const output = new Float32Array(length);
  for (let i = 0; i < length; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += input[j] ?? 0;
    output[i] = end > start ? sum / (end - start) : 0;
  }
  return output;
}

/** Joins the recorder's chunks into one buffer. */
export function concatSamples(chunks: readonly Float32Array[]): Float32Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/** Mono float samples in [-1, 1] as a complete WAV file: the 44-byte header, then PCM16. */
export function encodeWav(
  samples: Float32Array,
  sampleRate: number = WAV_RATE,
): Uint8Array<ArrayBuffer> {
  const dataBytes = samples.length * 2;
  const bytes = new Uint8Array(HEADER_BYTES + dataBytes);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);

  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i] ?? 0));
    view.setInt16(HEADER_BYTES + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return bytes;
}
